import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, TrendingUp, Users, Package, ShieldCheck, Zap, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CHECKOUT_URL = "https://pay.kiwify.com.br/SzzAeex";

const LandingPage = () => {
  const [mostrarFormFornecedor, setMostrarFormFornecedor] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [form, setForm] = useState({
    nome: "", email: "", telefone: "", cnpj: "", categoria: "", tempo_mercado: "", link_loja: "",
  });

  const enviarSolicitacao = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.email || !form.cnpj) {
      toast.error("Preencha nome, email e CNPJ.");
      return;
    }
    setEnviando(true);
    try {
      const dados = new FormData();
      Object.entries(form).forEach(([chave, valor]) => dados.append(chave, valor));
      const resp = await fetch(`${BACKEND_URL}/api/fornecedor/solicitar-cadastro`, {
        method: "POST",
        body: dados,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "Erro ao enviar solicitacao.");
      setEnviado(true);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-gray-950 text-white">
      <header className="border-b border-gray-800 px-6 py-4 flex items-center justify-between max-w-6xl mx-auto">
        <img src="/images/clickhub-logo.png" alt="ClickHub" className="h-8 object-contain" />
      </header>

      <section className="max-w-4xl mx-auto px-6 py-16 text-center">
        <h1 className="text-4xl sm:text-5xl font-bold mb-4">
          Venda em marketplaces sem sair de casa
        </h1>
        <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
          Conectamos afiliados a fornecedores de verdade. Você anuncia, a gente entrega. Comece a vender ainda hoje.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href={CHECKOUT_URL} target="_blank" rel="noreferrer">
            <Button className="bg-orange-500 hover:bg-orange-600 text-lg px-8 py-6 w-full sm:w-auto">
              Torne-se um Afiliado
            </Button>
          </a>
          <Button
            onClick={() => setMostrarFormFornecedor(true)}
            variant="outline"
            className="border-gray-700 text-lg px-8 py-6 w-full sm:w-auto"
          >
            Torne-se um Fornecedor
          </Button>
        </div>
      </section>

      <section className="bg-gray-900/50 border-y border-gray-800 py-16">
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
          <div>
            <TrendingUp className="w-8 h-8 text-orange-400 mx-auto mb-2" />
            <p className="text-2xl font-bold">+20</p>
            <p className="text-gray-500 text-sm">Marketplaces</p>
          </div>
          <div>
            <Users className="w-8 h-8 text-orange-400 mx-auto mb-2" />
            <p className="text-2xl font-bold">100%</p>
            <p className="text-gray-500 text-sm">Fornecedores verificados</p>
          </div>
          <div>
            <Package className="w-8 h-8 text-orange-400 mx-auto mb-2" />
            <p className="text-2xl font-bold">0</p>
            <p className="text-gray-500 text-sm">Investimento em estoque</p>
          </div>
          <div>
            <ShieldCheck className="w-8 h-8 text-orange-400 mx-auto mb-2" />
            <p className="text-2xl font-bold">24/7</p>
            <p className="text-gray-500 text-sm">Robo de vendas ativo</p>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="text-2xl font-bold text-center mb-10">Como funciona para o afiliado</h2>
        <div className="space-y-4">
          {[
            "Escolha um fornecedor e seus produtos",
            "Gere links e poste nas suas redes sociais",
            "O cliente compra, o fornecedor entrega",
            "Voce recebe sua comissao automaticamente",
          ].map((texto, i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center font-bold shrink-0">{i + 1}</div>
              <p className="text-gray-300">{texto}</p>
            </div>
          ))}
        </div>
      </section>

      {mostrarFormFornecedor && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto py-8">
          <Card className="bg-gray-900 border-gray-800 p-6 max-w-md w-full max-h-[85vh] overflow-y-auto">
            {enviado ? (
              <div className="text-center py-6">
                <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold mb-1">Solicitacao enviada!</h3>
                <p className="text-gray-400 text-sm">Vamos avaliar seu cadastro e responder por e-mail em breve.</p>
                <Button onClick={() => setMostrarFormFornecedor(false)} className="mt-4 bg-orange-500 hover:bg-orange-600">
                  Fechar
                </Button>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-semibold mb-1">Seja um fornecedor parceiro</h3>
                <p className="text-gray-400 text-sm mb-4">Preencha os dados abaixo. Vamos avaliar e responder por e-mail.</p>
                <form onSubmit={enviarSolicitacao} className="space-y-3">
                  <Input placeholder="Nome / Razao Social" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="bg-gray-800 border-gray-700" />
                  <Input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-gray-800 border-gray-700" />
                  <Input placeholder="Telefone" value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} className="bg-gray-800 border-gray-700" />
                  <Input placeholder="CNPJ" value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} className="bg-gray-800 border-gray-700" />
                  <Input placeholder="Categoria de produtos" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} className="bg-gray-800 border-gray-700" />
                  <Input placeholder="Tempo de mercado" value={form.tempo_mercado} onChange={(e) => setForm({ ...form, tempo_mercado: e.target.value })} className="bg-gray-800 border-gray-700" />
                  <Input placeholder="Link da loja / rede social" value={form.link_loja} onChange={(e) => setForm({ ...form, link_loja: e.target.value })} className="bg-gray-800 border-gray-700" />
                  <div className="flex gap-2 pt-2">
                    <Button type="button" onClick={() => setMostrarFormFornecedor(false)} variant="outline" className="border-gray-700 flex-1">Cancelar</Button>
                    <Button type="submit" disabled={enviando} className="bg-orange-500 hover:bg-orange-600 flex-1">
                      {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enviar"}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
